import React, { PropsWithChildren } from "react";

import { css, cx } from "@emotion/css";

import { FontSizes } from "@/assets/fonts.ts";
import { Icons } from "@/assets/icons.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import { FileModel } from "@/models/FileModel.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Button } from "@/uiKit/atoms/Button.tsx";
import { Typography } from "@/uiKit/atoms/Typography.tsx";

export type FileViewProps = PropsWithChildren<{
  id?: string;
  file: FileModel;
  onDelete?: () => void;
  className?: string;
}>;

export const FileView: React.FC<FileViewProps> = ({ id, file, children, onDelete = undefined, className }) => {
  const styles = useStyles(makeStyles);

  return (
    <div id={id} className={cx(styles.container, className)}>
      <Typography variant="body" className={styles.name}>
        {file.name}
      </Typography>
      <Button
        variant="text"
        data-action="common.file.download"
        className={styles.button}
        onClick={() => window.open(file.url, "_blank")}
        startIcon={<Icons.download className={styles.icon} />}
      />
      {onDelete && (
        <Button
          data-action="common.file.delete"
          variant="text"
          className={styles.button}
          onClick={onDelete}
          startIcon={<Icons.trash className={styles.icon} />}
        />
      )}
      {children}
    </div>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css`
    display: flex;
    flex-direction: row;
    align-items: center;
    width: 100%;
    gap: ${theme.spacing(4)};
  `,
  name: css({
    flexShrink: 1,
    minWidth: 0,
    textOverflow: "ellipsis",
    overflow: "clip",
  }),
  pdf: css`
    margin-top: ${theme.spacing(4)};
  `,
  button: css`
    padding: ${theme.spacing(2)};
    :hover {
      background-color: ${theme.palette.action.hover};
    }
    color: ${theme.palette.primary.main};
  `,
  icon: css`
    height: ${FontSizes.body};
    width: ${FontSizes.body};
    margin-right: 0;
  `,
});
